import { alpha } from '@mui/material/styles';

export const createComponents = (palette, isDark, primaryColor, secondaryColor) => ({
  MuiCssBaseline: {
    styleOverrides: {
      ':root': {
        colorScheme: isDark ? 'dark' : 'light',
      },
      'html': {
        height: '100%',
        scrollBehavior: 'smooth',
        // Custom scrollbar for document level
        '&::-webkit-scrollbar': {
          width: '12px',
          background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          background: isDark 
            ? 'rgba(255, 255, 255, 0.15)' 
            : 'rgba(255, 77, 79, 0.15)',
          borderRadius: '6px',
          border: '3px solid transparent',
          backgroundClip: 'padding-box',
          transition: 'all 0.2s ease',
          '&:hover': {
            background: isDark 
              ? 'rgba(255, 255, 255, 0.25)' 
              : 'rgba(255, 77, 79, 0.25)',
          },
          '&:active': {
            background: isDark 
              ? 'rgba(255, 255, 255, 0.35)' 
              : 'rgba(255, 77, 79, 0.35)',
          }
        },
        '&::-webkit-scrollbar-track': {
          background: 'transparent',
          border: 'none',
          borderRadius: '6px',
        },
        '&::-webkit-scrollbar-corner': {
          background: 'transparent',
        },
        scrollbarWidth: 'auto',
        scrollbarColor: isDark 
          ? 'rgba(255, 255, 255, 0.15) transparent' 
          : 'rgba(255, 77, 79, 0.15) transparent',
      },
      'body': {
        minHeight: '100%',
        margin: 0,
        padding: 0,
        backgroundColor: palette.background.default,
        color: palette.text.primary,
        '--bg-default': palette.background.default,
        '--bg-paper': palette.background.paper,
        '--text-primary': palette.text.primary,
        '--text-secondary': palette.text.secondary,
        '--primary-color': primaryColor,
        '--secondary-color': secondaryColor,
        '--divider-color': palette.divider,
        '--input-bg': isDark ? 'rgba(255, 255, 255, 0.05)' : '#f0f2f5',
      },
      '#root': {
        minHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
      },
      // Global page container styles
      '.page-container': {
        minHeight: '100vh',
        height: 'auto',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      },
      '.scrollable-content': {
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
        height: '100%',
        minHeight: 0,
        position: 'relative',
        scrollBehavior: 'smooth',
        WebkitOverflowScrolling: 'touch',
        // Custom scrollbar for main content
        '&::-webkit-scrollbar': {
          width: '12px',
          background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          background: isDark 
            ? 'rgba(255, 255, 255, 0.15)' 
            : 'rgba(255, 77, 79, 0.15)',
          borderRadius: '6px',
          border: '3px solid transparent',
          backgroundClip: 'padding-box',
          transition: 'all 0.2s ease',
          '&:hover': {
            background: isDark 
              ? 'rgba(255, 255, 255, 0.25)' 
              : 'rgba(255, 77, 79, 0.25)',
          },
          '&:active': {
            background: isDark 
              ? 'rgba(255, 255, 255, 0.35)' 
              : 'rgba(255, 77, 79, 0.35)',
          }
        },
        '&::-webkit-scrollbar-track': {
          background: 'transparent',
          border: 'none',
          borderRadius: '6px',
        },
        '&::-webkit-scrollbar-corner': {
          background: 'transparent',
        },
        scrollbarWidth: 'auto',
        scrollbarColor: isDark 
          ? 'rgba(255, 255, 255, 0.15) transparent' 
          : 'rgba(255, 77, 79, 0.15) transparent',
      },
      '.full-height-layout': {
        height: '100vh',
        maxHeight: '100vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      },
      '.main-content': {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        overflow: 'hidden',
      },
      '.page-content': {
        flex: 1,
        minHeight: 0,
        position: 'relative',
        // Allow both internal scrolling and document-level scrolling
        '&.scrollable': {
          overflowY: 'auto',
          overflowX: 'hidden',
          '&::-webkit-scrollbar': {
            width: '8px',
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: isDark 
              ? 'rgba(255, 255, 255, 0.2)' 
              : 'rgba(255, 77, 79, 0.2)',
            borderRadius: '20px',
            border: '2px solid transparent',
            backgroundClip: 'padding-box',
            transition: 'background 0.2s ease',
            '&:hover': {
              background: isDark 
                ? 'rgba(255, 255, 255, 0.3)' 
                : 'rgba(255, 77, 79, 0.3)',
            }
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
            border: 'none',
          },
          scrollbarWidth: 'thin',
          scrollbarColor: isDark 
            ? 'rgba(255, 255, 255, 0.2) transparent' 
            : 'rgba(255, 77, 79, 0.2) transparent',
        },
        // Default: allow document-level scrolling
        '&:not(.scrollable)': {
          minHeight: 'auto',
        }
      },
      // Content wrapper that matches header width constraints
      '.content-wrapper': {
        maxWidth: '1536px', // xl breakpoint - matches header Container maxWidth="xl"
        width: '100%',
        margin: '0 auto',
        padding: '0 16px', // Base padding
        '@media (min-width: 600px)': {
          padding: '0 24px', // sm and up
        },
      },
      '.sidebar-layout': {
        display: 'flex',
        height: '100vh',
        overflow: 'hidden',
      },
      '.sidebar': {
        width: '280px',
        flexShrink: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
        backgroundColor: palette.background.paper,
        borderRight: `1px solid ${palette.divider}`,
        '&::-webkit-scrollbar': {
          width: '6px',
          background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          background: isDark 
            ? 'rgba(255, 255, 255, 0.2)' 
            : 'rgba(255, 77, 79, 0.2)',
          borderRadius: '3px',
          '&:hover': {
            background: isDark 
              ? 'rgba(255, 255, 255, 0.3)' 
              : 'rgba(255, 77, 79, 0.3)',
          }
        },
        '&::-webkit-scrollbar-track': {
          background: 'transparent',
        },
        scrollbarWidth: 'thin',
      },
      '.main-area': {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        overflow: 'hidden',
      },
      '.fixed-header': {
        position: 'sticky',
        top: 0,
        zIndex: 1100,
        backgroundColor: palette.background.paper,
        borderBottom: `1px solid ${palette.divider}`,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
      },
      '.fixed-footer': {
        position: 'sticky',
        bottom: 0,
        zIndex: 1100,
        backgroundColor: palette.background.paper,
        borderTop: `1px solid ${palette.divider}`,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
      },
      // Responsive styles
      '@media (max-width: 600px)': {
        '.content-wrapper': {
          padding: '0 16px',
        },
        '.scrollable-content': {
          '&::-webkit-scrollbar': {
            width: '6px',
          },
        }
      },
      '@media (min-width: 600px)': {
        '.content-wrapper': {
          padding: '0 24px',
        }
      },
      '*': {
        transitionProperty: 'transform, box-shadow, opacity, border-radius, width, height !important',
        backfaceVisibility: 'hidden',
        WebkitBackfaceVisibility: 'hidden',
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
      },
      '*, *::before, *::after': {
        willChange: 'transform, opacity',
        transform: 'translateZ(0)',
        WebkitTransform: 'translateZ(0)'
      },
      '.MuiDialog-root, .MuiPopover-root, .MuiDrawer-root': {
        transform: 'translate3d(0,0,0)',
        WebkitTransform: 'translate3d(0,0,0)',
        backfaceVisibility: 'hidden',
        WebkitBackfaceVisibility: 'hidden',
        perspective: 1000,
        WebkitPerspective: 1000
      },
      // Global keyframes for animations
      '@keyframes fadeIn': {
        '0%': {
          opacity: 0,
          transform: 'translateY(10px)'
        },
        '100%': {
          opacity: 1,
          transform: 'translateY(0)'
        }
      },
      '@keyframes spin': {
        '0%': {
          transform: 'rotate(0deg)',
        },
        '100%': {
          transform: 'rotate(360deg)',
        },
      },
      '@keyframes pulseBoxShadow': {
        '0%': {
          boxShadow: `0 0 0 0 ${alpha(primaryColor, 0.7)}`
        },
        '70%': {
          boxShadow: `0 0 0 10px ${alpha(primaryColor, 0)}`
        },
        '100%': {
          boxShadow: `0 0 0 0 ${alpha(primaryColor, 0)}`
        }
      },
      '@keyframes construction': {
        '0%': {
          backgroundPosition: '0 0',
        },
        '100%': {
          backgroundPosition: '56px 56px',
        },
      },
      '@keyframes moveBackground': {
        '0%': {
          transform: 'translate(0, 0)'
        },
        '100%': {
          transform: 'translate(20px, 20px)'
        }
      },
      '@keyframes shimmer': {
        '100%': {
          transform: 'translateX(100%)',
        },
      },
      // Global animation keyframes for enhanced components
      '@keyframes snackbarSlideUp': {
        '0%': {
          opacity: 0,
          transform: 'translateY(20px)'
        },
        '100%': {
          opacity: 1,
          transform: 'translateY(0)'
        }
      },
    },
  },

  MuiPaper: {
    styleOverrides: {
      root: {
        backgroundImage: 'none',
        backgroundColor: 'var(--bg-paper)',
        transform: 'translate3d(0,0,0)',
        WebkitTransform: 'translate3d(0,0,0)',
        // Only add subtle shadow on hover, preserve any custom background colors
        '&:hover': {
          boxShadow: isDark 
            ? '0 4px 12px rgba(0, 0, 0, 0.1)' 
            : '0 4px 12px rgba(5, 3, 21, 0.06)',
        }
      },
    },
  },

  MuiCard: {
    styleOverrides: {
      root: {
        backgroundColor: 'var(--bg-paper)',
        borderRadius: 8,
        overflow: 'hidden',
        transition: 'all 0.2s ease-in-out'
      },
    },
  },

  MuiTextField: {
    defaultProps: {
      variant: 'outlined',
      size: 'small',
    },
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          backgroundColor: 'var(--input-bg)',
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: isDark ? 
              alpha('#FFFFFF', 0.08) : 
              '#ede8e4'
          },
          '&.Mui-focused': {
            backgroundColor: isDark ? 
              alpha('#FFFFFF', 0.12) : 
              '#e8e2dd'
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: alpha(primaryColor, 0.5)
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: primaryColor,
            borderWidth: '2px'
          }
        },
        '& .MuiInputLabel-root': {
          color: palette.text.secondary,
          '&.Mui-focused': {
            color: primaryColor
          }
        },
        '& .MuiInputBase-input': {
          color: palette.text.primary
        }
      },
    },
  },

  MuiSelect: {
    defaultProps: {
      variant: 'outlined',
      size: 'small',
    },
    styleOverrides: {
      root: {
        backgroundColor: 'var(--input-bg)',
        '&:hover': {
          backgroundColor: isDark ? 
            alpha('#FFFFFF', 0.08) : 
            '#ede8e4'
        },
        '&.Mui-focused': {
          backgroundColor: isDark ? 
            alpha('#FFFFFF', 0.12) : 
            '#e8e2dd'
        }
      },
      select: {
        '&:focus': {
          backgroundColor: 'transparent'
        }
      }
    },
  },

  MuiFormControl: {
    styleOverrides: {
      root: {
        '& .MuiInputLabel-root': {
          color: palette.text.secondary,
          '&.Mui-focused': {
            color: primaryColor
          }
        }
      }
    }
  },

  MuiButton: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        transition: 'transform 0.2s ease-out, box-shadow 0.2s ease-out',
        '&:hover': {
          backgroundColor: isDark ?
            alpha(primaryColor, 0.12) :
            '#fff5f5'
        }
      },
      contained: {
        '&:hover': {
          backgroundColor: isDark ?
            alpha(primaryColor, 0.8) :
            '#ff3333'
        }
      },
      containedSecondary: {
        '&:hover': {
          backgroundColor: isDark ?
            alpha(secondaryColor, 0.8) :
            alpha(secondaryColor, 0.9)
        }
      },
      outlined: {
        borderColor: isDark ?
          alpha(primaryColor, 0.5) :
          '#ffcccc',
        '&:hover': {
          backgroundColor: isDark ?
            alpha(primaryColor, 0.12) :
            '#fff0f0',
          borderColor: primaryColor
        }
      },
      outlinedSecondary: {
        borderColor: isDark ?
          alpha(secondaryColor, 0.5) :
          alpha(secondaryColor, 0.3),
        '&:hover': {
          backgroundColor: isDark ?
            alpha(secondaryColor, 0.12) :
            alpha(secondaryColor, 0.08),
          borderColor: secondaryColor
        }
      }
    },
  },

  MuiIconButton: {
    styleOverrides: {
      root: {
        transition: 'transform 0.2s ease-out, box-shadow 0.2s ease-out',
        '&:hover': {
          backgroundColor: isDark ?
            alpha('#FFFFFF', 0.08) :
            '#f8f4f1'
        }
      },
    },
  },

  MuiChip: {
    styleOverrides: {
      root: {
        transition: 'transform 0.2s ease-out, box-shadow 0.2s ease-out',
        '&:hover': {
          backgroundColor: isDark ?
            alpha(primaryColor, 0.2) :
            '#fff0f0'
        }
      },
      colorSecondary: {
        backgroundColor: alpha(secondaryColor, 0.1),
        color: isDark ? '#FFFFFF' : '#050315',
        '&:hover': {
          backgroundColor: alpha(secondaryColor, 0.2)
        }
      },
      outlined: {
        borderColor: isDark ?
          alpha('#FFFFFF', 0.3) :
          '#e8e2dd',
        '&:hover': {
          borderColor: primaryColor
        }
      },
      outlinedSecondary: {
        borderColor: alpha(secondaryColor, 0.5),
        color: isDark ? '#FFFFFF' : '#050315',
        '&:hover': {
          borderColor: secondaryColor,
          backgroundColor: alpha(secondaryColor, 0.08)
        }
      }
    },
  },

  MuiTypography: {
    styleOverrides: {
      root: {
        transition: 'color 0.1s ease-out',
      },
    },
  },

  MuiDivider: {
    styleOverrides: {
      root: {
        borderColor: palette.divider,
      },
    },
  },

  MuiAppBar: {
    styleOverrides: {
      root: {
        backgroundColor: palette.background.paper,
        color: palette.text.primary,
        boxShadow: `0 1px 3px ${alpha(palette.text.primary, 0.12)}`,
      },
    },
  },

  MuiDrawer: {
    styleOverrides: {
      paper: {
        backgroundColor: alpha(palette.background.paper, 0.98),
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        borderRight: `1px solid ${alpha(palette.divider, 0.8)}`,
        borderColor: palette.divider,
        boxShadow: `
          0 8px 32px ${alpha(palette.common.black, 0.12)},
          inset 0 0 0 1px ${alpha(palette.common.white, 0.05)}
        `
      },
      paperAnchorRight: {
        borderRight: 'none',
        borderLeft: `1px solid ${alpha(palette.divider, 0.8)}`
      }
    }
  },

  MuiDialog: {
    styleOverrides: {
      paper: {
        backgroundColor: palette.background.paper,
        borderRadius: 8,
        border: `1px solid ${palette.divider}`,
        boxShadow: `
          0 24px 48px ${alpha(palette.common.black, 0.15)},
          0 8px 16px ${alpha(palette.common.black, 0.1)}
        `,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        overflow: 'hidden',
        // Glassmorphism effect for dialogs
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: isDark 
            ? 'linear-gradient(135deg, rgba(255,255,255,0.02) 0%, rgba(255,255,255,0.05) 100%)'
            : 'linear-gradient(135deg, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0.4) 100%)',
          pointerEvents: 'none',
          zIndex: -1
        }
      },
      backdrop: {
        backgroundColor: alpha(palette.common.black, 0.6),
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)'
      }
    },
  },

  MuiDialogTitle: {
    styleOverrides: {
      root: {
        fontFamily: 'StrawberrySansDisplay',
        fontWeight: 600,
        fontSize: '1.25rem',
        color: palette.text.primary,
        borderBottom: `1px solid ${palette.divider}`,
        paddingBottom: '16px',
        marginBottom: '16px'
      }
    }
  },

  MuiDialogContent: {
    styleOverrides: {
      root: {
        padding: '20px 24px',
        '&:first-of-type': {
          paddingTop: '20px'
        }
      }
    }
  },

  MuiDialogActions: {
    styleOverrides: {
      root: {
        padding: '16px 24px',
        borderTop: `1px solid ${palette.divider}`,
        gap: '12px',
        '& .MuiButton-root': {
          minWidth: '80px'
        }
      }
    }
  },

  MuiMenu: {
    styleOverrides: {
      paper: {
        backgroundColor: palette.background.paper,
        border: `1px solid ${palette.divider}`,
        borderRadius: 8,
        boxShadow: `0 4px 20px ${alpha(palette.common.black, 0.15)}`,
      },
    },
  },

  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        backgroundColor: isDark ? 
          alpha('#000000', 0.9) : 
          alpha('#2f3337', 0.9),
        color: '#ffffff',
        fontSize: '0.75rem',
        borderRadius: 6,
        padding: '8px 12px',
        boxShadow: `0 4px 12px ${alpha(palette.common.black, 0.15)}`,
      },
      arrow: {
        color: isDark ? 
          alpha('#000000', 0.9) : 
          alpha('#2f3337', 0.9),
      }
    },
  },

  MuiSnackbar: {
    styleOverrides: {
      root: {
        zIndex: 1400,
        '& .MuiPaper-root, & .MuiAlert-root': {
          position: 'relative',
          minWidth: 280,
          maxWidth: 420,
          borderRadius: 14,
          overflow: 'hidden',
          animation: 'snackbarSlideUp 0.35s cubic-bezier(0.21, 0.53, 0.35, 1)',
          boxShadow: `0 4px 20px ${alpha(palette.common.black, 0.15)}`,
          '&::before': { display: 'none' },
          '&::after': { display: 'none' },
        },
        '& .MuiSnackbarContent-root': {
          minWidth: 'auto',
          backgroundColor: 'transparent',
          fontWeight: 500,
          fontSize: '0.875rem',
          padding: '12px 16px',
          fontFamily: 'StrawberrySansText',
          position: 'relative',
          zIndex: 2,
          '& .MuiSnackbarContent-message': {
            padding: 0,
            fontFamily: 'StrawberrySansText'
          },
          '& .MuiSnackbarContent-action': {
            marginRight: 0,
            paddingLeft: '16px'
          }
        }
      }
    }
  },

  MuiSnackbarContent: {
    styleOverrides: {
      root: {
        backgroundColor: 'transparent',
        boxShadow: 'none'
      }
    }
  },

  MuiAlert: {
    styleOverrides: {
      root: {
        borderRadius: 14,
        fontFamily: 'StrawberrySansText',
        fontSize: '0.875rem',
        padding: '12px 16px',
        alignItems: 'center',
        '& .MuiAlert-icon': {
          fontSize: '1.25rem',
          padding: 0,
          marginRight: 12,
          opacity: 0.95
        },
        '& .MuiAlert-message': {
          fontFamily: 'StrawberrySansText',
          fontSize: '0.875rem',
          fontWeight: 500,
          padding: 0
        },
        '& .MuiAlert-action': {
          alignItems: 'center',
          paddingLeft: 8,
          '& .MuiIconButton-root': {
            color: 'rgba(255,255,255,0.9)',
            padding: 4,
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.12)',
              color: '#fff'
            }
          }
        }
      },
      filled: {
        '&.MuiAlert-filledSuccess': {
          backgroundColor: palette.success.main,
          color: '#fff',
          boxShadow: `0 4px 20px ${alpha(palette.success.main, 0.35)}`,
          '& .MuiAlert-icon': { color: '#fff' }
        },
        '&.MuiAlert-filledError': {
          backgroundColor: palette.error.main,
          color: '#fff',
          boxShadow: `0 4px 20px ${alpha(palette.error.main, 0.35)}`,
          '& .MuiAlert-icon': { color: '#fff' }
        },
        '&.MuiAlert-filledWarning': {
          backgroundColor: palette.warning.main,
          color: '#fff',
          boxShadow: `0 4px 20px ${alpha(palette.warning.main, 0.35)}`,
          '& .MuiAlert-icon': { color: '#fff' }
        },
        '&.MuiAlert-filledInfo': {
          backgroundColor: palette.info.main,
          color: '#fff',
          boxShadow: `0 4px 20px ${alpha(palette.info.main, 0.35)}`,
          '& .MuiAlert-icon': { color: '#fff' }
        }
      }
    }
  },

  MuiTable: {
    styleOverrides: {
      root: {
        '& .MuiTableCell-root': {
          borderColor: palette.divider,
        }
      }
    }
  },

  MuiTableHead: {
    styleOverrides: {
      root: {
        '& .MuiTableCell-root': {
          backgroundColor: alpha(palette.background.default, 0.5),
          fontWeight: 600,
          fontSize: '0.875rem',
          color: palette.text.secondary,
          textTransform: 'uppercase',
          letterSpacing: '0.05em'
        }
      }
    }
  },

  MuiTableRow: {
    styleOverrides: {
      root: {
        '&:hover': {
          backgroundColor: alpha(primaryColor, 0.04)
        },
        '&:last-child .MuiTableCell-root': {
          borderBottom: 'none'
        }
      }
    }
  },

  MuiTableCell: {
    styleOverrides: {
      root: {
        fontSize: '0.875rem',
        padding: '12px 16px'
      }
    }
  },

  MuiList: {
    styleOverrides: {
      root: {
        padding: 0,
        '& .MuiListItem-root': {
          transition: 'background-color 0.2s ease',
          '&:hover': {
            backgroundColor: alpha(primaryColor, 0.04)
          }
        }
      }
    }
  },

  MuiListItem: {
    styleOverrides: {
      root: {
        '&.Mui-selected': {
          backgroundColor: alpha(primaryColor, 0.08),
          '&:hover': {
            backgroundColor: alpha(primaryColor, 0.12)
          }
        }
      }
    }
  },

  MuiListItemIcon: {
    styleOverrides: {
      root: {
        color: palette.text.secondary,
        minWidth: 40,
        '& .MuiSvgIcon-root': {
          fontSize: '1.25rem'
        }
      }
    }
  },

  MuiAvatar: {
    styleOverrides: {
      root: {
        backgroundColor: alpha(primaryColor, 0.1),
        color: primaryColor,
        fontWeight: 600
      },
      colorDefault: {
        backgroundColor: alpha(palette.text.secondary, 0.1),
        color: palette.text.secondary
      }
    }
  },

  MuiBadge: {
    styleOverrides: {
      badge: {
        backgroundColor: primaryColor,
        color: '#ffffff',
        fontWeight: 600,
        fontSize: '0.75rem'
      },
      colorSecondary: {
        backgroundColor: secondaryColor,
        color: '#ffffff'
      }
    }
  },

  MuiTabs: {
    styleOverrides: {
      root: {
        '& .MuiTab-root': {
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.875rem',
          color: palette.text.secondary,
          '&.Mui-selected': {
            color: primaryColor
          }
        },
        '& .MuiTabs-indicator': {
          backgroundColor: primaryColor,
          height: 3,
          borderRadius: '3px 3px 0 0'
        }
      }
    }
  },

  MuiAccordion: {
    styleOverrides: {
      root: {
        backgroundColor: 'transparent',
        border: `1px solid ${palette.divider}`,
        borderRadius: 8,
        '&:before': {
          display: 'none'
        },
        '&.Mui-expanded': {
          margin: 0,
          boxShadow: `0 4px 12px ${alpha(palette.common.black, 0.08)}`
        }
      }
    }
  },

  MuiAccordionSummary: {
    styleOverrides: {
      root: {
        backgroundColor: alpha(palette.background.default, 0.3),
        borderRadius: '8px 8px 0 0',
        '&:hover': {
          backgroundColor: alpha(primaryColor, 0.04)
        },
        '&.Mui-expanded': {
          minHeight: 48,
          backgroundColor: alpha(primaryColor, 0.05)
        },
        '& .MuiAccordionSummary-content': {
          margin: '12px 0',
          '&.Mui-expanded': {
            margin: '12px 0'
          }
        }
      }
    }
  },

  MuiBreadcrumbs: {
    styleOverrides: {
      root: {
        '& .MuiBreadcrumbs-separator': {
          color: palette.text.secondary
        }
      }
    }
  },

  MuiLinearProgress: {
    styleOverrides: {
      root: {
        borderRadius: 4,
        backgroundColor: alpha(primaryColor, 0.1),
        '& .MuiLinearProgress-bar': {
          borderRadius: 4,
          backgroundColor: primaryColor
        }
      }
    }
  },

  MuiCircularProgress: {
    styleOverrides: {
      root: {
        color: primaryColor
      }
    }
  },

  MuiFab: {
    styleOverrides: {
      root: {
        backgroundColor: primaryColor,
        color: '#ffffff',
        boxShadow: `
          0 6px 20px ${alpha(primaryColor, 0.3)},
          0 2px 8px ${alpha(palette.common.black, 0.1)}
        `,
        '&:hover': {
          backgroundColor: alpha(primaryColor, 0.8),
          boxShadow: `
            0 8px 25px ${alpha(primaryColor, 0.4)},
            0 4px 12px ${alpha(palette.common.black, 0.15)}
          `,
          transform: 'translateY(-2px)'
        },
        '&:active': {
          transform: 'translateY(0px)',
          boxShadow: `
            0 4px 15px ${alpha(primaryColor, 0.3)},
            0 2px 6px ${alpha(palette.common.black, 0.1)}
          `
        }
      }
    }
  },

  MuiSwitch: {
    styleOverrides: {
      root: {
        '& .MuiSwitch-switchBase.Mui-checked': {
          color: primaryColor,
          '& + .MuiSwitch-track': {
            backgroundColor: alpha(primaryColor, 0.5)
          }
        }
      }
    }
  },

  MuiSlider: {
    styleOverrides: {
      root: {
        color: primaryColor,
        '& .MuiSlider-thumb': {
          backgroundColor: primaryColor,
          '&:hover': {
            boxShadow: `0 0 0 8px ${alpha(primaryColor, 0.16)}`
          }
        },
        '& .MuiSlider-track': {
          backgroundColor: primaryColor
        },
        '& .MuiSlider-rail': {
          backgroundColor: alpha(primaryColor, 0.2)
        }
      }
    }
  },

  MuiCheckbox: {
    styleOverrides: {
      root: {
        color: palette.text.secondary,
        '&.Mui-checked': {
          color: primaryColor
        }
      }
    }
  },

  MuiRadio: {
    styleOverrides: {
      root: {
        color: palette.text.secondary,
        '&.Mui-checked': {
          color: primaryColor
        }
      }
    }
  },

  MuiFormControlLabel: {
    styleOverrides: {
      root: {
        '& .MuiFormControlLabel-label': {
          fontSize: '0.875rem',
          color: palette.text.primary
        }
      }
    }
  },

  MuiSkeleton: {
    styleOverrides: {
      root: {
        backgroundColor: alpha(primaryColor, 0.08),
        '&::after': {
          background: `linear-gradient(
            90deg,
            transparent,
            ${alpha(primaryColor, 0.2)},
            transparent
          )`
        }
      }
    }
  },

  // Missing components found in the app
  MuiTablePagination: {
    styleOverrides: {
      root: {
        color: palette.text.secondary,
        '& .MuiTablePagination-selectLabel': {
          fontSize: '0.875rem'
        },
        '& .MuiTablePagination-displayedRows': {
          fontSize: '0.875rem'
        }
      },
      select: {
        '&:focus': {
          backgroundColor: alpha(primaryColor, 0.08)
        }
      },
      actions: {
        '& .MuiIconButton-root': {
          color: palette.text.secondary,
          '&:hover': {
            color: primaryColor,
            backgroundColor: alpha(primaryColor, 0.08)
          },
          '&.Mui-disabled': {
            color: palette.text.disabled
          }
        }
      }
    }
  },

  MuiPagination: {
    styleOverrides: {
      root: {
        '& .MuiPaginationItem-root': {
          color: palette.text.secondary,
          '&:hover': {
            backgroundColor: alpha(primaryColor, 0.08)
          },
          '&.Mui-selected': {
            backgroundColor: primaryColor,
            color: '#ffffff',
            '&:hover': {
              backgroundColor: alpha(primaryColor, 0.8)
            }
          }
        }
      }
    }
  },

  MuiSpeedDial: {
    styleOverrides: {
      fab: {
        backgroundColor: primaryColor,
        color: '#ffffff',
        '&:hover': {
          backgroundColor: alpha(primaryColor, 0.8)
        }
      }
    }
  },

  MuiSpeedDialAction: {
    styleOverrides: {
      fab: {
        backgroundColor: palette.background.paper,
        color: palette.text.primary,
        border: `1px solid ${palette.divider}`,
        '&:hover': {
          backgroundColor: alpha(primaryColor, 0.08),
          borderColor: primaryColor
        }
      }
    }
  },

  MuiCollapse: {
    styleOverrides: {
      root: {
        '& .MuiCollapse-wrapper': {
          transition: 'all 0.3s ease'
        }
      }
    }
  },

  MuiPopover: {
    styleOverrides: {
      paper: {
        backgroundColor: alpha(palette.background.paper, 0.95),
        border: `1px solid ${alpha(palette.divider, 0.8)}`,
        borderRadius: 12,
        boxShadow: `
          0 12px 40px ${alpha(palette.common.black, 0.15)},
          0 4px 16px ${alpha(palette.common.black, 0.1)},
          inset 0 0 0 1px ${alpha(palette.common.white, 0.1)}
        `,
        backdropFilter: 'blur(12px) saturate(180%)',
        WebkitBackdropFilter: 'blur(12px) saturate(180%)',
        overflow: 'hidden',
        // Enhanced glassmorphism for popovers
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: isDark 
            ? 'linear-gradient(135deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%)'
            : 'linear-gradient(135deg, rgba(255,255,255,0.7) 0%, rgba(255,255,255,0.3) 100%)',
          pointerEvents: 'none',
          zIndex: -1,
          borderRadius: 'inherit'
        }
      }
    }
  },

  MuiBackdrop: {
    styleOverrides: {
      root: {
        backgroundColor: alpha(palette.common.black, 0.6),
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        transition: 'all 0.3s ease'
      }
    }
  },

  MuiModal: {
    styleOverrides: {
      root: {
        '& .MuiBackdrop-root': {
          backgroundColor: alpha(palette.common.black, 0.6),
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)'
        }
      }
    }
  },

  MuiSwipeableDrawer: {
    styleOverrides: {
      paper: {
        backgroundColor: alpha(palette.background.paper, 0.98),
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)'
      }
    }
  },

  MuiStack: {
    styleOverrides: {
      root: {
        // Ensure proper spacing and alignment
      }
    }
  },

  MuiContainer: {
    styleOverrides: {
      root: {
        // Ensure consistent container behavior
        '@media (min-width: 600px)': {
          paddingLeft: '24px',
          paddingRight: '24px'
        }
      }
    }
  },

  MuiGrid: {
    styleOverrides: {
      root: {
        // Ensure consistent grid behavior
      }
    }
  },

  MuiLink: {
    styleOverrides: {
      root: {
        color: primaryColor,
        textDecoration: 'none',
        '&:hover': {
          textDecoration: 'underline',
          color: alpha(primaryColor, 0.8)
        }
      }
    }
  },

  MuiToggleButton: {
    styleOverrides: {
      root: {
        color: palette.text.secondary,
        border: `1px solid ${palette.divider}`,
        '&:hover': {
          backgroundColor: alpha(primaryColor, 0.08),
          borderColor: alpha(primaryColor, 0.5)
        },
        '&.Mui-selected': {
          backgroundColor: alpha(primaryColor, 0.12),
          color: primaryColor,
          borderColor: primaryColor,
          '&:hover': {
            backgroundColor: alpha(primaryColor, 0.16)
          }
        }
      }
    }
  },

  MuiToggleButtonGroup: {
    styleOverrides: {
      root: {
        '& .MuiToggleButton-root': {
          '&:not(:first-of-type)': {
            borderLeft: `1px solid ${palette.divider}`
          }
        }
      }
    }
  },

  MuiRating: {
    styleOverrides: {
      root: {
        color: primaryColor,
        '& .MuiRating-iconEmpty': {
          color: alpha(primaryColor, 0.2)
        }
      }
    }
  },

  MuiStepper: {
    styleOverrides: {
      root: {
        backgroundColor: 'transparent'
      }
    }
  },

  MuiStep: {
    styleOverrides: {
      root: {
        '& .MuiStepLabel-root': {
          '& .MuiStepLabel-iconContainer': {
            '& .MuiStepIcon-root': {
              color: alpha(primaryColor, 0.2),
              '&.Mui-active': {
                color: primaryColor
              },
              '&.Mui-completed': {
                color: primaryColor
              }
            }
          }
        }
      }
    }
  },

  // Additional popup/overlay components
  MuiBottomSheet: {
    styleOverrides: {
      root: {
        backgroundColor: alpha(palette.background.paper, 0.95),
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        borderTopLeftRadius: 16,
        borderTopRightRadius: 16,
        boxShadow: `
          0 -8px 32px ${alpha(palette.common.black, 0.12)},
          inset 0 0 0 1px ${alpha(palette.common.white, 0.1)}
        `
      }
    }
  },

  MuiAccordionDetails: {
    styleOverrides: {
      root: {
        padding: '16px 24px',
        backgroundColor: palette.background.paper,
        borderRadius: '0 0 8px 8px'
      }
    }
  },

  // Enhanced Menu styling
  MuiMenuList: {
    styleOverrides: {
      root: {
        padding: '8px',
        '& .MuiMenuItem-root': {
          borderRadius: 6,
          margin: '2px 0',
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: alpha(primaryColor, 0.08),
            transform: 'translateX(2px)'
          },
          '&.Mui-selected': {
            backgroundColor: alpha(primaryColor, 0.12),
            '&:hover': {
              backgroundColor: alpha(primaryColor, 0.16)
            }
          }
        }
      }
    }
  },

  MuiMenuItem: {
    styleOverrides: {
      root: {
        fontSize: '0.875rem',
        padding: '8px 12px',
        minHeight: 'auto',
        '& .MuiListItemIcon-root': {
          minWidth: 36,
          color: palette.text.secondary
        },
        '&.Mui-selected .MuiListItemIcon-root': {
          color: primaryColor
        }
      }
    }
  }
}); 